import { Check } from 'lucide-react'

const plans = [
  {
    name: 'Basis',
    price: '199',
    period: 'kr./md.',
    note: 'Op til 10 lejemål — faktureres årligt',
    description: 'Til den private udlejer med op til 10 lejemål.',
    cta: 'Start 30 dages gratis prøve',
    highlight: false,
    features: [
      'Op til 10 lejemål',
      'Digital lejekontrakt (A9/A10)',
      'MitID-signering',
      'BBR-integration',
      'Lejeroprettelse med CPR-kryptering',
      'E-mail notifikationer',
      'E-mail support',
    ],
  },
  {
    name: 'Pro',
    price: '599',
    period: 'kr./md.',
    note: 'Op til 50 lejemål — faktureres årligt',
    description: 'Til den seriøse udlejer og mindre ejendomsadministrator.',
    cta: 'Start 30 dages gratis prøve',
    highlight: true,
    features: [
      'Op til 50 lejemål',
      'Alt i Basis',
      'Månedlig huslejeopkrævning',
      'Manuel betalingsregistrering',
      'e-conomic bogføringseksport',
      'Lejerportal',
      'Prioriteret support',
    ],
  },
  {
    name: 'Enterprise',
    price: 'Aftales',
    period: '',
    note: '50+ lejemål',
    description: 'Til ejendomsselskaber med store porteføljer og særlige krav.',
    cta: 'Kontakt os',
    highlight: false,
    features: [
      'Ubegrænset lejemål',
      'Alt i Pro',
      'Multi-CVR og selskabsstruktur',
      'Bogføringsdimensioner',
      'Dedikeret onboarding',
      'SLA-aftale',
      'API-adgang',
    ],
  },
]

const faq = [
  {
    q: 'Er der binding?',
    a: 'Nej. Abonnementet faktureres årligt og kan opsiges til næste betalingsperiode. Ingen binding ud over den betalte periode.',
  },
  {
    q: 'Hvad sker der med mine data, hvis jeg opsiger?',
    a: 'Du kan eksportere alle data til CSV/PDF inden opsigelse. Data slettes efter 90 dage i overensstemmelse med GDPR.',
  },
  {
    q: 'Er Boligflow GDPR-kompatibel?',
    a: 'Ja. CPR-numre krypteres med AES-256-GCM og logges aldrig i klartekst. Audit-log er aktiv fra dag ét.',
  },
  {
    q: 'Hvornår er systemet klar til brug?',
    a: 'Vi er i åben beta med digital kontraktoprettelse og MitID-signering. Huslejeopkrævning og e-conomic-integration er under udvikling og forventes klar inden for de næste måneder.',
  },
  {
    q: 'Hvad koster prøveperioden?',
    a: 'Ingenting. 30 dage gratis, ingen kreditkort kræves. Du opgraderer kun hvis du vil fortsætte.',
  },
]

export default function PricingPage() {
  return (
    <>
      {/* Hero */}
      <section className="bg-navy-950 text-white py-20 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-5">Priser</h1>
          <p className="text-navy-200 text-lg">
            30 dages gratis prøve. Ingen kreditkort. Faktureres årligt.
          </p>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="py-20 px-6 bg-cream">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 items-start">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-2xl p-8 border ${
                  plan.highlight
                    ? 'bg-navy-950 text-white border-navy-700 ring-2 ring-navy-500'
                    : 'bg-white border-navy-100'
                }`}
              >
                {plan.highlight && (
                  <span className="inline-block bg-navy-600 text-navy-100 text-xs font-semibold px-3 py-1 rounded-full mb-4 tracking-wide uppercase">
                    Mest valgt
                  </span>
                )}
                <h2 className={`text-xl font-bold mb-1 ${plan.highlight ? 'text-white' : 'text-navy-950'}`}>
                  {plan.name}
                </h2>
                <p className={`text-sm mb-6 ${plan.highlight ? 'text-navy-300' : 'text-navy-500'}`}>
                  {plan.description}
                </p>
                <div className="mb-6">
                  <span className={`text-4xl font-bold ${plan.highlight ? 'text-white' : 'text-navy-950'}`}>
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span className={`text-sm ml-1 ${plan.highlight ? 'text-navy-300' : 'text-navy-500'}`}>
                      {plan.period}
                    </span>
                  )}
                  <p className={`text-xs mt-1 ${plan.highlight ? 'text-navy-400' : 'text-navy-400'}`}>
                    {plan.note}
                  </p>
                </div>
                <a
                  href="https://admin.boligflow.toragroup.dk"
                  className={`block text-center font-semibold px-6 py-3 rounded-lg mb-8 transition-colors ${
                    plan.highlight
                      ? 'bg-white text-navy-950 hover:bg-cream'
                      : 'bg-navy-950 text-white hover:bg-navy-800'
                  }`}
                >
                  {plan.cta}
                </a>
                <ul className="space-y-3">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check
                        size={16}
                        className={`mt-0.5 shrink-0 ${plan.highlight ? 'text-navy-300' : 'text-navy-500'}`}
                      />
                      <span className={plan.highlight ? 'text-navy-200' : 'text-navy-700'}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-navy-950 text-center mb-12">Ofte stillede spørgsmål</h2>
          <dl className="space-y-8">
            {faq.map(({ q, a }) => (
              <div key={q}>
                <dt className="text-navy-950 font-semibold mb-2">{q}</dt>
                <dd className="text-navy-600 leading-relaxed">{a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </>
  )
}
